import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import { IpAllowlistView } from "./components/IpAllowlistView";
import { useIpAllowlist, useIpAllowlistMutation } from "./queries";
import type { IpAllowlistMutation } from "./queries";
import { parseIpAllowlistId } from "./schema";

function paramsFor(id?: number) {
  const params = new URLSearchParams();
  if (id) params.set("allowlistId", String(id));
  return params;
}

type Notice = { readonly tone: "information" | "success"; readonly text: string };

export function IpAllowlistPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useAdminSession();
  const search = searchParams.toString();
  const selectedId = useMemo(() => parseIpAllowlistId(new URLSearchParams(search).get("allowlistId")), [search]);
  const list = useIpAllowlist();
  const mutation = useIpAllowlistMutation();
  const [notice, setNotice] = useState<Notice>();
  const selected = list.data?.find((row) => row.id === selectedId);
  const authorities = session.authSession?.authorities ?? [];

  useEffect(() => {
    const canonical = paramsFor(selectedId);
    if (canonical.toString() !== search) setSearchParams(canonical, { replace: true });
  }, [search, selectedId, setSearchParams]);

  useEffect(() => {
    if (!list.data || list.isFetching || !selectedId || selected) return;
    setSearchParams(paramsFor(), { replace: true });
  }, [list.data, list.isFetching, selected, selectedId, setSearchParams]);

  const complete = async (input: IpAllowlistMutation, result: unknown) => {
    if (input.kind === "create") {
      const created = result as Schemas["AdminIpAllowlistResponse"];
      setNotice({ tone: "success", text: `${created.cidr} 범위를 등록했습니다. 서버에서 발급한 허용목록 ID는 ${created.id}입니다.` });
      setSearchParams(paramsFor(created.id));
      return;
    }
    setNotice({ tone: "information", text: `허용목록 ID ${input.id}의 상태를 비활성으로 변경했습니다. 서버 목록을 다시 조회했습니다.` });
  };

  return <IpAllowlistView
    items={list.data}
    listState={list.isPending ? "loading" : list.isError ? "error" : "ready"}
    selectedId={selectedId}
    selected={selected}
    canManage={Boolean(authorities.includes("ROLE_SUPER") && authorities.includes("ADMIN_MANAGE"))}
    notice={notice}
    onSelect={(id) => { setNotice(undefined); setSearchParams(paramsFor(id)); }}
    onClearSelection={() => setSearchParams(paramsFor())}
    onRetryList={async () => {
      const result = await list.refetch();
      if (result.error) throw result.error;
    }}
    onMutation={(input) => mutation.mutateAsync(input)}
    onMutationComplete={complete}
  />;
}
